/**
 * Landing-page search deep-links.
 * Availability: `0` = Any time (online + offline); `only` = Available now
 * (hide listeners who are not online). Format is client-side.
 */

import {
  AVAILABLE_NOW_OFF,
  AVAILABLE_NOW_ONLY,
  AVAILABLE_NOW_PARAM,
  filterListingsByAvailableNow,
} from './listingReviews';
import { SESSION_FORMATS, offeredFormatsFromListing } from './sessionPricing';
import { TOPICS_PARAM, parseTopicsParam } from './sessionTopics';
import { LISTENING_STYLES_PARAM, parseListeningStylesParam } from './listeningStyles';

export const OFFERED_FORMAT_PARAM = 'format';

export const LANDING_AVAILABILITY = {
  ANY: 'any',
  NOW: 'now',
};

export const parseOfferedFormatParam = value => {
  if (value === 'text' || value === 'chat') {
    return 'chat';
  }
  if (SESSION_FORMATS.includes(value)) {
    return value;
  }
  return null;
};

export const listingOffersFormat = (listing, format) => {
  if (!format) {
    return true;
  }
  return offeredFormatsFromListing(listing).includes(format);
};

export const filterListingsByOfferedFormat = (listings = [], format) => {
  const parsed = parseOfferedFormatParam(format);
  if (!parsed) {
    return listings;
  }
  return listings.filter(listing => listingOffersFormat(listing, parsed));
};

export const clientSearchParamsFromQuery = params => {
  const next = {};
  if (params?.[AVAILABLE_NOW_PARAM] === '0' || params?.[AVAILABLE_NOW_PARAM] === 0) {
    next[AVAILABLE_NOW_PARAM] = '0';
  } else if (
    params?.[AVAILABLE_NOW_PARAM] === AVAILABLE_NOW_ONLY ||
    params?.[AVAILABLE_NOW_PARAM] === 'true' ||
    params?.[AVAILABLE_NOW_PARAM] === true ||
    params?.[AVAILABLE_NOW_PARAM] === '1' ||
    params?.[AVAILABLE_NOW_PARAM] === 1
  ) {
    next[AVAILABLE_NOW_PARAM] = AVAILABLE_NOW_ONLY;
  }
  const format = parseOfferedFormatParam(params?.[OFFERED_FORMAT_PARAM]);
  if (format) {
    next[OFFERED_FORMAT_PARAM] = format;
  }
  const topics = parseTopicsParam(params?.[TOPICS_PARAM]);
  if (topics.length) {
    next[TOPICS_PARAM] = topics.join(',');
  }
  const styles = parseListeningStylesParam(params?.[LISTENING_STYLES_PARAM]);
  if (styles.length) {
    next[LISTENING_STYLES_PARAM] = styles.join(',');
  }
  return next;
};

/**
 * Build /s query params from the landing search form.
 * Available now filters to online listeners (`only`). Any time shows everyone (`0`).
 */
export const buildLandingSearchQuery = ({ keywords, availability, format } = {}) => {
  const params = {};
  const trimmed = typeof keywords === 'string' ? keywords.trim() : '';
  if (trimmed) {
    params.keywords = trimmed;
  }
  // Any time is the default. Available now is only set when the user picks it.
  if (availability === LANDING_AVAILABILITY.NOW) {
    params[AVAILABLE_NOW_PARAM] = AVAILABLE_NOW_ONLY;
  } else {
    params[AVAILABLE_NOW_PARAM] = AVAILABLE_NOW_OFF;
  }
  const parsedFormat = parseOfferedFormatParam(format);
  if (parsedFormat) {
    params[OFFERED_FORMAT_PARAM] = parsedFormat;
  }
  return params;
};

/**
 * Featured strip format chip. Callers pass Any time for availability so
 * Available now (a Search filter) cannot hide a paid Featured profile.
 */
export const filterLandingFeaturedListings = (
  listings = [],
  { availability, format, presenceMap = {} } = {}
) => {
  const byFormat = filterListingsByOfferedFormat(listings, format);
  return filterListingsByAvailableNow(byFormat, presenceMap, {
    [AVAILABLE_NOW_PARAM]:
      availability === LANDING_AVAILABILITY.NOW ? AVAILABLE_NOW_ONLY : AVAILABLE_NOW_OFF,
  });
};

/**
 * Intention cards deep-link with keywords that already appear in TEST listener bios.
 */
export const LANDING_INTENTION_CARDS = [
  { id: 'easygoing', keywords: 'unhurried', topic: 'everyday-conversation' },
  { id: 'talkThrough', keywords: 'talk through', topic: 'advice-perspective' },
  { id: 'work', keywords: 'work', topic: 'work-career' },
  { id: 'hobbies', keywords: 'walk', topic: 'hobbies-interests' },
  { id: 'encouragement', keywords: 'kind', topic: 'confidence-self-esteem' },
  { id: 'quiet', keywords: 'quiet', topic: 'everyday-conversation' },
  { id: 'vent', keywords: 'vent', topic: 'stress-venting' },
  { id: 'lonely', keywords: 'lonely', topic: 'loneliness' },
  { id: 'relationships', keywords: 'relationships', topic: 'relationships-dating' },
  { id: 'advice', keywords: 'advice', topic: 'advice-perspective' },
];
